import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useInfiniteQuery, useQuery } from "react-query"

import { emptySelection } from "src/components/DataTable/selection"

import useDebounce from "src/hooks/use_debounce"

// Infer type from a default value for URL serialization
function inferType(value) {
  if (Array.isArray(value)) return "array"
  if (typeof value === "boolean") return "boolean"
  if (typeof value === "number") return "number"
  return "string"
}

// Read a filter value from URL search params based on its type
function readFilterFromUrl(key, type, searchParams) {
  switch (type) {
    case "array":
      return searchParams.getAll(`${key}[]`)
    case "boolean":
      return searchParams.get(key) === "true"
    case "number": {
      const val = searchParams.get(key)
      return val !== null ? Number(val) : null
    }
    default: {
      return searchParams.get(key) || null
    }
  }
}

// Build URL search params from filter state, omitting default/empty values
function writeFiltersToUrl(searchParams, filters, defaults, types) {
  Object.keys(defaults).forEach((key) => {
    const type = types[key]
    const value = filters[key]
    const defaultValue = defaults[key]

    // Remove existing params for this key
    if (type === "array") {
      searchParams.delete(`${key}[]`)
    } else {
      searchParams.delete(key)
    }

    // Only write non-default values
    if (isDefaultValue(value, defaultValue, type)) return

    switch (type) {
      case "array":
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((v) => searchParams.append(`${key}[]`, v))
        }
        break
      case "boolean":
        searchParams.set(key, String(value))
        break
      case "number":
        if (value !== 0) searchParams.set(key, String(value))
        break
      default:
        if (value) searchParams.set(key, value)
    }
  })
}

// Check if a value equals its default
function isDefaultValue(value, defaultValue, type) {
  if (type === "array") {
    return (
      !Array.isArray(value) ||
      value.length === 0 ||
      (Array.isArray(defaultValue) &&
        value.length === defaultValue.length &&
        value.every((v, i) => v === defaultValue[i]))
    )
  }
  return value === defaultValue
}

// Build the params object for fetchFn, stripping empty/default values
function buildFetchParams(page, sortKey, sortDirection, filters, defaults) {
  const params = {}

  if (page > 1) params.page = page
  if (sortKey) params.sort = sortKey
  if (sortDirection && sortKey) params.direction = sortDirection

  Object.entries(filters).forEach(([key, value]) => {
    const defaultValue = defaults[key]
    const type = inferType(defaultValue ?? "")

    if (isDefaultValue(value, defaultValue, type)) return

    // Include truthy/non-empty values
    if (type === "boolean") {
      params[key] = value
    } else if (type === "array" && Array.isArray(value) && value.length > 0) {
      params[key] = value
    } else if (type === "number" && value !== 0) {
      params[key] = value
    } else if (type === "string" && value) {
      params[key] = value
    }
  })

  return params
}

export default function useDatatable({
  queryKey,
  fetchFn,
  defaultFilters = {},
  defaultSort,
  filters: controlledFilters,
  onFiltersChange,
  fetchAllIds,
  enabled = true,
  idKey = "id",
  staleTime = 30000,
  syncFiltersToUrl = true,
  infinite = false,
  refetchInterval,
}) {
  // Normalize queryKey to array
  const queryKeyArray = useMemo(
    () => (Array.isArray(queryKey) ? queryKey : [queryKey]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(queryKey)]
  )

  // Compute filter types from defaults (stable across renders)
  const filterTypes = useRef(
    Object.fromEntries(
      Object.entries(defaultFilters).map(([key, val]) => [key, inferType(val)])
    )
  ).current

  const isControlled = controlledFilters !== undefined

  // Read initial state from URL
  const initialState = useRef(() => {
    const searchParams = new URLSearchParams(window.location.search)

    const page = parseInt(searchParams.get("page")) || 1
    // The URL wins when it names a sort; otherwise fall back to defaultSort so
    // the header can show the state the server is already applying.
    const urlSortKey = searchParams.get("sort")
    const sortKey = urlSortKey || defaultSort?.column || null
    const sortDirection = urlSortKey
      ? searchParams.get("direction")
      : defaultSort?.direction ?? null

    // Read filter values from URL, falling back to defaults
    const filters = {}
    Object.entries(defaultFilters).forEach(([key, defaultValue]) => {
      const type = filterTypes[key]
      const urlValue = syncFiltersToUrl
        ? readFilterFromUrl(key, type, searchParams)
        : null

      if (urlValue !== null && urlValue !== undefined) {
        if (
          type === "array" &&
          Array.isArray(urlValue) &&
          urlValue.length > 0
        ) {
          filters[key] = urlValue
        } else if (type === "boolean") {
          filters[key] = urlValue
        } else if (type === "number" && !isNaN(urlValue)) {
          filters[key] = urlValue
        } else if (type === "string" && urlValue) {
          filters[key] = urlValue
        } else {
          filters[key] = defaultValue
        }
      } else {
        filters[key] = defaultValue
      }
    })

    return { page, sortKey, sortDirection, filters }
  }).current

  // Call the lazy initializer once
  const lazyInit = useRef(null)
  if (lazyInit.current === null) {
    lazyInit.current =
      typeof initialState === "function" ? initialState() : initialState
  }
  const init = lazyInit.current

  const [page, setPageState] = useState(init.page)
  const [sortKey, setSortKey] = useState(init.sortKey)
  const [sortDirection, setSortDirection] = useState(init.sortDirection)
  const [internalFilters, setInternalFilters] = useState(init.filters)
  const [selection, setSelection] = useState(emptySelection())

  // Effective filter values (controlled or internal)
  const effectiveFilters = isControlled ? controlledFilters : internalFilters

  // In controlled mode, reset page to 1 when filters change externally
  // (e.g. stat card clicks that bypass useDatatable's own setFilter/setFilters)
  const serializedControlledFilters = isControlled
    ? JSON.stringify(controlledFilters)
    : null
  const prevControlledFiltersRef = useRef(serializedControlledFilters)

  useEffect(() => {
    if (
      isControlled &&
      prevControlledFiltersRef.current !== null &&
      serializedControlledFilters !== prevControlledFiltersRef.current
    ) {
      setPageState(1)
      const url = new URL(window.location.href)
      url.searchParams.delete("page")
      window.history.replaceState(null, null, url.toString())
    }
    prevControlledFiltersRef.current = serializedControlledFilters
  }, [isControlled, serializedControlledFilters])

  // Debounced URL sync
  const [debouncedSyncUrl] = useDebounce((params) => {
    const url = new URL(window.location.href)
    const searchParams = url.searchParams

    // Sync page/sort/direction
    ;["page", "sort", "direction"].forEach((key) => {
      if (params[key]) {
        searchParams.set(key, params[key])
      } else {
        searchParams.delete(key)
      }
    })

    // Sync filters
    if (syncFiltersToUrl && params._filters) {
      writeFiltersToUrl(
        searchParams,
        params._filters,
        defaultFilters,
        filterTypes
      )
    }

    window.history.replaceState(null, null, url.toString())
  }, 300)

  // Sync URL whenever state changes
  const syncUrl = useCallback(
    (newPage, newSortKey, newSortDirection, newFilters) => {
      const urlParams = {
        ...(newPage > 1 && { page: newPage }),
        ...(newSortKey && { sort: newSortKey }),
        ...(newSortDirection && newSortKey && { direction: newSortDirection }),
        _filters: newFilters,
      }
      debouncedSyncUrl(urlParams)
    },
    [debouncedSyncUrl]
  )

  // Page setter that also syncs URL
  const setPage = useCallback(
    (newPage) => {
      setPageState(newPage)
      syncUrl(newPage, sortKey, sortDirection, effectiveFilters)
    },
    [sortKey, sortDirection, effectiveFilters, syncUrl]
  )

  // Sort change handler — resets page to 1
  const handleSortChange = useCallback(
    ({ key, direction }) => {
      setPageState(1)
      setSortKey(key)
      setSortDirection(direction)
      syncUrl(1, key, direction, effectiveFilters)
    },
    [effectiveFilters, syncUrl]
  )

  // Filter setters
  const setFilter = useCallback(
    (key, value) => {
      if (isControlled && onFiltersChange) {
        onFiltersChange({ ...controlledFilters, [key]: value })
      } else {
        setInternalFilters((prev) => {
          const next = { ...prev, [key]: value }
          syncUrl(1, sortKey, sortDirection, next)
          return next
        })
      }
      setPageState(1)
    },
    [
      isControlled,
      onFiltersChange,
      controlledFilters,
      sortKey,
      sortDirection,
      syncUrl,
    ]
  )

  const setFilters = useCallback(
    (patch) => {
      if (isControlled && onFiltersChange) {
        onFiltersChange({ ...controlledFilters, ...patch })
      } else {
        setInternalFilters((prev) => {
          const next = { ...prev, ...patch }
          syncUrl(1, sortKey, sortDirection, next)
          return next
        })
      }
      setPageState(1)
    },
    [
      isControlled,
      onFiltersChange,
      controlledFilters,
      sortKey,
      sortDirection,
      syncUrl,
    ]
  )

  const clearFilter = useCallback(
    (key) => {
      const defaultValue = defaultFilters[key]
      if (defaultValue === undefined) return

      if (isControlled && onFiltersChange) {
        onFiltersChange({ ...controlledFilters, [key]: defaultValue })
      } else {
        setInternalFilters((prev) => {
          const next = { ...prev, [key]: defaultValue }
          syncUrl(1, sortKey, sortDirection, next)
          return next
        })
      }
      setPageState(1)
    },
    [
      defaultFilters,
      isControlled,
      onFiltersChange,
      controlledFilters,
      sortKey,
      sortDirection,
      syncUrl,
    ]
  )

  const clearAllFilters = useCallback(() => {
    if (isControlled && onFiltersChange) {
      onFiltersChange({ ...defaultFilters })
    } else {
      setInternalFilters({ ...defaultFilters })
      syncUrl(1, sortKey, sortDirection, defaultFilters)
    }
    setPageState(1)
  }, [
    defaultFilters,
    isControlled,
    onFiltersChange,
    sortKey,
    sortDirection,
    syncUrl,
  ])

  // Active filters — only non-default entries
  const activeFilters = useMemo(() => {
    const active = []
    Object.entries(effectiveFilters).forEach(([key, value]) => {
      const defaultValue = defaultFilters[key]
      const type = filterTypes[key] || inferType(defaultValue ?? "")
      if (!isDefaultValue(value, defaultValue, type)) {
        active.push({ key, value })
      }
    })
    return active
  }, [effectiveFilters, defaultFilters, filterTypes])

  // Build params for fetchFn
  const fetchParams = useMemo(
    () =>
      buildFetchParams(
        page,
        sortKey,
        sortDirection,
        effectiveFilters,
        defaultFilters
      ),
    [page, sortKey, sortDirection, effectiveFilters, defaultFilters]
  )

  // Build params for fetchAllIds (filters only, no page/sort)
  const allIdsFilterParams = useMemo(
    () => buildFetchParams(1, null, null, effectiveFilters, defaultFilters),
    [effectiveFilters, defaultFilters]
  )

  // Standard data query (non-infinite mode)
  const standardQuery = useQuery(
    [...queryKeyArray, page, sortKey, sortDirection, effectiveFilters],
    () => fetchFn(fetchParams),
    {
      keepPreviousData: true,
      staleTime,
      refetchOnWindowFocus: false,
      enabled: enabled && !infinite,
      refetchInterval,
    }
  )

  // Infinite data query — uses react-query's useInfiniteQuery for native page accumulation
  const infiniteQuery = useInfiniteQuery(
    [...queryKeyArray, "infinite", sortKey, sortDirection, effectiveFilters],
    ({ pageParam = 1 }) => {
      const params = buildFetchParams(
        pageParam,
        sortKey,
        sortDirection,
        effectiveFilters,
        defaultFilters
      )
      return fetchFn(params)
    },
    {
      staleTime,
      refetchOnWindowFocus: false,
      enabled: enabled && infinite,
      refetchInterval,
      getNextPageParam: (lastPage) => {
        const { currentPage, totalPages } = lastPage.pagination
        return currentPage < totalPages ? currentPage + 1 : undefined
      },
    }
  )

  // Unified accessors based on mode
  const lastInfinitePage =
    infiniteQuery.data?.pages?.[infiniteQuery.data.pages.length - 1]
  const data = infinite ? lastInfinitePage : standardQuery.data
  const isLoading = infinite ? infiniteQuery.isLoading : standardQuery.isLoading
  const isFetching = infinite
    ? infiniteQuery.isFetching
    : standardQuery.isFetching
  const isError = infinite ? infiniteQuery.isError : standardQuery.isError
  const error = infinite ? infiniteQuery.error : standardQuery.error
  const isPreviousData = infinite ? false : standardQuery.isPreviousData

  // Records: flatten all pages in infinite mode, single page in standard mode
  const records = infinite
    ? infiniteQuery.data?.pages?.flatMap((p) => p?.data || []) || []
    : data?.data || []

  const hasNextPage = infinite ? !!infiniteQuery.hasNextPage : undefined

  // Queue fetchNextPage calls that arrive before the first page has loaded.
  // react-query's native fetchNextPage requires at least one page to compute
  // the next pageParam, so we defer the call until data is available.
  const pendingFetchNextPage = useRef(false)

  useEffect(() => {
    if (
      infinite &&
      pendingFetchNextPage.current &&
      infiniteQuery.data?.pages?.length > 0 &&
      !infiniteQuery.isFetching
    ) {
      pendingFetchNextPage.current = false
      infiniteQuery.fetchNextPage()
    }
  }, [infinite, infiniteQuery])

  const fetchNextPageStable = useCallback(() => {
    if (!infinite) return
    if (infiniteQuery.data?.pages?.length > 0) {
      infiniteQuery.fetchNextPage()
    } else {
      pendingFetchNextPage.current = true
    }
  }, [infinite, infiniteQuery])

  // All-IDs query for cross-page selection
  const { data: allIdsData } = useQuery(
    [...queryKeyArray, "allIds", effectiveFilters],
    () => fetchAllIds(allIdsFilterParams),
    {
      refetchOnWindowFocus: false,
      staleTime,
      enabled: enabled && !!fetchAllIds,
    }
  )

  const allSelectableIds = allIdsData ?? undefined

  // Page change handler for DataTableV2
  const onPageChange = useCallback(
    (pageData) => {
      setPage(pageData.selected)
    },
    [setPage]
  )

  // Spread-ready props for <DataTableV2>
  const tableProps = useMemo(
    () =>
      infinite
        ? {
            idKey,
            selection,
            onSelectionChange: setSelection,
            totalCount: data?.pagination?.totalSelectableCount,
            allSelectableIds,
            infinite: true,
            pagination: false,
            sortConfig: {
              sortKey,
              sortDirection,
              onSortChange: handleSortChange,
            },
            isLoading,
          }
        : {
            idKey,
            selection,
            onSelectionChange: setSelection,
            totalCount: data?.pagination?.totalSelectableCount,
            allSelectableIds,
            pagination: true,
            page,
            numberOfPages: data?.pagination?.totalPages || 1,
            onPageChange,
            sortConfig: {
              sortKey,
              sortDirection,
              onSortChange: handleSortChange,
            },
            isLoading: isLoading || (isFetching && isPreviousData),
          },
    [
      infinite,
      idKey,
      selection,
      data?.pagination?.totalSelectableCount,
      data?.pagination?.totalPages,
      allSelectableIds,
      page,
      onPageChange,
      sortKey,
      sortDirection,
      handleSortChange,
      isLoading,
      isFetching,
      isPreviousData,
    ]
  )

  return {
    data,
    records,
    isLoading,
    isFetching,
    isError,
    error,

    filters: effectiveFilters,
    defaultFilters,
    setFilter,
    setFilters,
    clearFilter,
    clearAllFilters,
    activeFilters,
    fetchParams,

    selection,
    setSelection,

    handleSortChange,

    fetchNextPage: fetchNextPageStable,
    hasNextPage,
    isFetchingNextPage: !!infiniteQuery.isFetchingNextPage,

    tableProps,
  }
}
